import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { LogOut, User, Building2, ChevronDown, ShieldCheck, BadgeCheck, Heart, Shield, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const login = () => navigate("/login");
  const setLang = (lng) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem("easystay-lang", lng); } catch {}
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <img src="/logo.png" alt="EasyStay" className="h-9 w-9 rounded-xl object-cover shadow-sm" />
          <span className="font-display text-xl font-bold tracking-tight text-slate-900">
            Easy<span className="text-copper">Stay</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <Link to="/search" data-testid="nav-search" className="text-sm font-medium text-slate-700 hover:text-copper">
            {t("nav.explore")}
          </Link>
          <Link to="/host" data-testid="nav-host" className="text-sm font-medium text-slate-700 hover:text-copper">
            {t("nav.host")}
          </Link>
          <Link to="/advertise" data-testid="nav-advertise" className="text-sm font-medium text-slate-700 hover:text-copper">
            {t("nav.advertise")}
          </Link>
          <Link to="/terms/guest" data-testid="nav-terms" className="text-sm font-medium text-slate-700 hover:text-copper">
            {t("nav.terms")}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          {/* Language switcher (English ↔ 中文) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher"
                aria-label={t("nav.language")}
                className="hidden items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-copper hover:text-forest sm:flex"
              >
                <Globe className="h-3.5 w-3.5" />
                {i18n.language?.startsWith("zh") ? "中" : "EN"}
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                data-testid="lang-en"
                onClick={() => setLang("en")}
                className={i18n.language?.startsWith("en") ? "font-semibold text-copper" : ""}
              >
                English
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="lang-zh"
                onClick={() => setLang("zh")}
                className={i18n.language?.startsWith("zh") ? "font-semibold text-copper" : ""}
              >
                简体中文
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden sm:flex overflow-hidden rounded-full border border-slate-200 bg-white">
            <button
              onClick={() => setCurrency("ZMW")}
              data-testid="currency-zmw"
              className={`px-3 py-1.5 text-xs font-semibold transition ${currency === "ZMW" ? "bg-forest text-white" : "text-slate-600 hover:text-forest"}`}
            >
              ZMW
            </button>
            <button
              onClick={() => setCurrency("USD")}
              data-testid="currency-usd"
              className={`px-3 py-1.5 text-xs font-semibold transition ${currency === "USD" ? "bg-forest text-white" : "text-slate-600 hover:text-forest"}`}
            >
              USD
            </button>
            <button
              onClick={() => setCurrency("CNY")}
              data-testid="currency-cny"
              title="Chinese Yuan (人民币)"
              className={`px-3 py-1.5 text-xs font-semibold transition ${currency === "CNY" ? "bg-forest text-white" : "text-slate-600 hover:text-forest"}`}
            >
              CNY
            </button>
          </div>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1.5 hover:border-copper">
                  <div className="relative">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-copper/10 text-copper">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                    {user.is_verified && (
                      <span data-testid="verified-badge-nav" className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-1 ring-white">
                        <BadgeCheck className="h-4 w-4 fill-copper text-white" />
                      </span>
                    )}
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2 text-sm">
                  <div className="flex items-center gap-1 font-semibold">
                    {user.name}
                    {user.is_verified && <BadgeCheck className="h-4 w-4 fill-copper text-white" data-testid="verified-badge-menu" />}
                  </div>
                  <div className="text-xs text-slate-500">{user.email}</div>
                  {!user.is_verified && (
                    <button onClick={() => navigate("/verify")} data-testid="menu-verify-cta"
                      className="mt-2 flex items-center gap-1 rounded-full bg-copper/10 px-3 py-1 text-xs font-semibold text-copper hover:bg-copper hover:text-white">
                      <ShieldCheck className="h-3.5 w-3.5" /> {t("nav.verifyCta")}
                    </button>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/my/bookings")} data-testid="menu-my-bookings">
                  {t("nav.myBookings")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/my/favourites")} data-testid="menu-my-favourites">
                  <Heart className="mr-2 h-4 w-4" /> {t("nav.favourites")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/verify")} data-testid="menu-verify">
                  <ShieldCheck className="mr-2 h-4 w-4" /> {user.is_verified ? t("nav.identityVerified") : t("nav.identityVerify")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/host/dashboard")} data-testid="menu-host-dashboard">
                  <Building2 className="mr-2 h-4 w-4" /> {t("nav.hostDashboard")}
                </DropdownMenuItem>
                {user.is_admin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Shield className="mr-2 h-4 w-4" /> {t("nav.admin")}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => navigate("/register")} data-testid="nav-register-btn" className="btn-outline text-sm hidden sm:inline-block">
                {t("nav.signUp")}
              </button>
              <button onClick={login} data-testid="nav-login-btn" className="btn-primary text-sm">
                {t("nav.signIn")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
